/**
 * Auth Store (Zustand)
 * Verwaltet Authentifizierung, User-Daten und Projektauswahl.
 *
 * Verwendet Immer-Middleware (wie annotation/scene stores).
 * Tokens werden in localStorage gespeichert.
 */

import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { apiFetch } from '../lib/apiBase'

// ─── Types ───────────────────────────────────────────────────────────────────

export interface AuthUser {
  id: string
  email: string
  username: string
  firstName?: string
  lastName?: string
  position?: string
  roles: { id: string; name: string }[]
  groups: { id: string; name: string }[]
  permissions?: string[]
}

export interface Project {
  id: string
  name: string
  code?: string
  description?: string
  status?: string
  pointClouds?: { id: string; name: string; path?: string; localPath?: string; url?: string; panoramaUrl?: string }[]
  etlServer?: { id: string; name: string; apiUrl: string; isDefault: boolean } | null
  audits?: { id: string }[] | number
  notes?: { id: string }[] | number
  documents?: { id: string }[] | number
  updatedAt?: string
  thumbnailUrl?: string
  thumbnailHigh?: string
  thumbnailMedium?: string
  thumbnailSmall?: string
  latitude?: number
  longitude?: number
  address?: string
}

interface AuthState {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  currentProjectId: string | null
  currentProject: Project | null
  projects: Project[]

  login: (email: string, password: string) => Promise<void>
  loginWithAccessToken: (token: string) => Promise<void>
  logout: () => Promise<void>
  loadUser: () => Promise<void>
  selectProject: (projectId: string) => Promise<void>
  loadProjects: () => Promise<void>
  loadProjectThumbnails: () => Promise<void>
  updateProjectThumbnail: (projectId: string, thumbs: { thumbnailSmall?: string | null; thumbnailMedium?: string | null; thumbnailHigh?: string | null }) => void
  updateUser: (data: Partial<Pick<AuthUser, 'email' | 'username' | 'firstName' | 'lastName' | 'position'>>, password?: string) => Promise<void>
}

// ─── Store ───────────────────────────────────────────────────────────────────

export const useAuthStore = create<AuthState>()(
  immer((set, get) => ({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
    currentProjectId: localStorage.getItem('currentProjectId') || null,
    currentProject: null,
    projects: [],

    login: async (email, password) => {
      set((s) => { s.isLoading = true; s.error = null })
      try {
        const res = await apiFetch('/api/auth/login', {
          method: 'POST',
          body: JSON.stringify({ email, password }),
        })
        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          throw new Error(body?.error?.message || body?.detail || 'Anmeldung fehlgeschlagen')
        }
        const json = await res.json()
        const data = json.data || json // Handle { success, data } wrapper
        localStorage.setItem('accessToken', data.accessToken)
        localStorage.setItem('refreshToken', data.refreshToken)
        set((s) => {
          s.user = data.user
          s.isAuthenticated = true
          s.isLoading = false
        })
        // Load projects after login
        await get().loadProjects()
      } catch (err: any) {
        set((s) => {
          s.error = err.message || 'Anmeldung fehlgeschlagen'
          s.isLoading = false
        })
        throw err
      }
    },

    loginWithAccessToken: async (token) => {
      set((s) => { s.isLoading = true; s.error = null })
      try {
        if (!token) throw new Error('Access-Token erforderlich')
        // Token vorab als accessToken im LocalStorage hinterlegen, damit
        // apiFetch den Authorization-Header automatisch mitschickt.
        localStorage.setItem('accessToken', token)
        localStorage.removeItem('refreshToken')
        const res = await apiFetch('/api/auth/me')
        if (!res.ok) {
          localStorage.removeItem('accessToken')
          const body = await res.json().catch(() => ({}))
          throw new Error(body?.error?.message || 'Access-Token ungültig')
        }
        const json = await res.json()
        const user = json.data || json
        set((s) => {
          s.user = user
          s.isAuthenticated = true
          s.isLoading = false
        })
        await get().loadProjects()
      } catch (err: any) {
        set((s) => {
          s.error = err.message || 'Anmeldung fehlgeschlagen'
          s.isLoading = false
        })
        throw err
      }
    },

    logout: async () => {
      try {
        await apiFetch('/api/auth/logout', { method: 'POST' })
      } catch { /* ignore */ }
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('currentProjectId')
      set((s) => {
        s.user = null
        s.isAuthenticated = false
        s.isLoading = false
        s.error = null
        s.currentProjectId = null
        s.currentProject = null
        s.projects = []
      })
    },

    loadUser: async () => {
      const token = localStorage.getItem('accessToken')
      if (!token) {
        set((s) => { s.isAuthenticated = false; s.isLoading = false })
        return
      }
      try {
        const res = await apiFetch('/api/auth/me')
        if (!res.ok) throw new Error('Unauthorized')
        const json = await res.json()
        const user = json.data || json
        set((s) => {
          s.user = user
          s.isAuthenticated = true
          s.isLoading = false
        })
        // Gespeichertes Projekt nachladen
        const savedProjectId = localStorage.getItem('currentProjectId')
        if (savedProjectId) {
          try { await get().selectProject(savedProjectId) } catch { /* Projekt ggf. nicht mehr zugreifbar */ }
        }
      } catch {
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        set((s) => {
          s.user = null
          s.isAuthenticated = false
          s.isLoading = false
        })
      }
    },

    selectProject: async (projectId) => {
      try {
        const res = await apiFetch(`/api/projects/${projectId}/tree`)
        if (!res.ok) throw new Error(`Projekt laden fehlgeschlagen: HTTP ${res.status}`)
        const json = await res.json()
        const project = json.data || json
        localStorage.setItem('currentProjectId', projectId)
        set((s) => {
          s.currentProjectId = projectId
          s.currentProject = project
        })
      } catch (err: any) {
        set((s) => { s.error = err.message || 'Projekt laden fehlgeschlagen' })
        throw err
      }
    },

    loadProjects: async () => {
      try {
        const res = await apiFetch('/api/projects/my')
        if (!res.ok) throw new Error(`Projekte laden fehlgeschlagen: HTTP ${res.status}`)
        const json = await res.json()
        const incoming: Project[] = Array.isArray(json.data || json) ? (json.data || json) : (json.data?.data || [])
        set((s) => {
          // Thumbnails aus dem bestehenden State beibehalten
          const thumbCache: Record<string, { sm?: string; md?: string; hi?: string }> = {}
          for (const p of s.projects) {
            if (p.thumbnailSmall || p.thumbnailMedium || p.thumbnailHigh) {
              thumbCache[p.id] = { sm: p.thumbnailSmall, md: p.thumbnailMedium, hi: p.thumbnailHigh }
            }
          }
          for (const p of incoming) {
            const cached = thumbCache[p.id]
            if (cached) {
              if (!p.thumbnailSmall && cached.sm) p.thumbnailSmall = cached.sm
              if (!p.thumbnailMedium && cached.md) p.thumbnailMedium = cached.md
              if (!p.thumbnailHigh && cached.hi) p.thumbnailHigh = cached.hi
            }
          }
          s.projects = incoming
        })
      } catch (err: any) {
        set((s) => { s.error = err.message || 'Projekte laden fehlgeschlagen' })
      }
    },

    loadProjectThumbnails: async () => {
      const projects = get().projects
      if (!projects.length) return

      // Parallel: je Projekt die erste Scene laden → Thumbnail extrahieren
      const results = await Promise.allSettled(
        projects.map(async (p) => {
          try {
            const res = await apiFetch(`/api/projects/${p.id}/scenes`)
            if (!res.ok) return null
            const json = await res.json()
            const scenes: any[] = Array.isArray(json) ? json : json.data || []
            // Bevorzugt die als Start markierte Szene, sonst erste mit Thumbnail
            const hasThumb = (s: any) => s.thumbnailHigh || s.thumbnailMedium || s.thumbnailSmall
            const scene = scenes.find((s: any) => s.isStartScene && hasThumb(s))
              ?? scenes.find(hasThumb)
            if (!scene) return null
            return {
              projectId: p.id,
              thumbnailHigh: scene.thumbnailHigh || null,
              thumbnailMedium: scene.thumbnailMedium || null,
              thumbnailSmall: scene.thumbnailSmall || null,
            }
          } catch { return null }
        })
      )

      set((s) => {
        for (const r of results) {
          if (r.status !== 'fulfilled' || !r.value) continue
          const { projectId, thumbnailHigh, thumbnailMedium, thumbnailSmall } = r.value
          const proj = s.projects.find((p) => p.id === projectId)
          if (proj) {
            if (thumbnailHigh) proj.thumbnailHigh = thumbnailHigh
            if (thumbnailMedium) proj.thumbnailMedium = thumbnailMedium
            if (thumbnailSmall) proj.thumbnailSmall = thumbnailSmall
          }
        }
      })
    },

    updateProjectThumbnail: (projectId, thumbs) => {
      set((s) => {
        const proj = s.projects.find((p) => p.id === projectId)
        if (!proj) return
        if (thumbs.thumbnailHigh) proj.thumbnailHigh = thumbs.thumbnailHigh
        if (thumbs.thumbnailMedium) proj.thumbnailMedium = thumbs.thumbnailMedium
        if (thumbs.thumbnailSmall) proj.thumbnailSmall = thumbs.thumbnailSmall
      })
    },

    updateUser: async (data, password) => {
      const user = get().user
      if (!user) throw new Error('Nicht angemeldet')
      const body: Record<string, unknown> = { ...data }
      if (password) body.password = password
      const res = await apiFetch(`/api/auth/me`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err?.error?.message || err?.detail || 'Speichern fehlgeschlagen')
      }
      const json = await res.json()
      const updated = json.data || json
      set((s) => {
        if (s.user) {
          if (updated.email) s.user.email = updated.email
          if (updated.username) s.user.username = updated.username
          if (updated.firstName !== undefined) s.user.firstName = updated.firstName
          if (updated.lastName !== undefined) s.user.lastName = updated.lastName
          if (updated.position !== undefined) s.user.position = updated.position
        }
      })
    },
  }))
)

// Expose store on window for projectUrl() helper
;(window as any).__authStore = useAuthStore
